import type { components } from "@contracts/generated/editor-service";

import {
  OMISSION_REASON_LABELS,
  OMISSION_REASONS,
  type OmissionReason,
  type PreviewRunState,
} from "./previewInsights";

/**
 * Preview Insights — reading ONE `prompt_call` event (#1099).
 *
 * `GET …/preview-runs/{ref}/events?kind=prompt_call&limit=1` locates the
 * first prompt call of a run as a COMPACT event (its bodies by reference,
 * never text), and `GET …/events/{eventRef}` is the one authorized read that
 * carries those bodies inline (approved design §7.4, §8.1). Both projections
 * are Brain's `clinical-preview-trace.v1`, proxied as JSON because this
 * repository deliberately does NOT vendor Brain's contract — restating its
 * event families here would be the hand-copied contract the design's §8.3
 * forbids. This module reads that JSON into a shape the Prompt Inspector can
 * render, with the same posture `previewInsights.ts` takes for the envelope:
 *
 *  - tolerant about MEMBERS, strict about MEANING. A member the event does
 *    not carry reads as `null` and renders as "not reported"; a value this
 *    build cannot place — a kind that is not `prompt_call`, an outcome,
 *    encoding, visibility, state, role or omission reason outside its closed
 *    list — makes the whole event unreadable, and unreadable is rendered as
 *    unreadable rather than as a partial prompt. Absent and present-but-unknown
 *    are different facts and are never folded into one;
 *  - a body policy withholds is never shown. A `restricted_technical` slot is
 *    omitted at source by the producer's own rule; one that carries text, or
 *    a reference to fetch, is refused whole — whatever the event around it
 *    says — rather than rendered. An omitted body of any class that still
 *    names a reference is refused for the same reason: withheld means
 *    nothing to fetch;
 *  - nothing here infers, fabricates or "improves" a fact the producer did not
 *    state. "The model was not invoked" is an EMITTED event with a reason code
 *    (`invocation.outcome`), never an absent one, and it is read as such;
 *  - a body is presented as exact only when it is exact. The producer measures
 *    `byteSize` on the bytes it carried; this module re-measures the text it
 *    received and keeps the two readings side by side, so a body that arrived
 *    shorter than declared is never labelled the exact effective prompt;
 *  - the text itself is never transformed. `segmentBidiControls` splits a body
 *    around the Unicode bidirectional control characters so the renderer can
 *    isolate and mark each one, and it round-trips byte for byte — the pieces
 *    concatenate back to the input.
 *
 * Pure and hermetic on purpose: it is the part of the feature the fast PR
 * gate (`src/lib`) proves on every push.
 */

export type PreviewRunEventPageResponse =
  components["schemas"]["PreviewRunEventPage"];
export type PreviewRunEventDetailResponse =
  components["schemas"]["PreviewRunEventDetail"];
export type PreviewTraceEventKind =
  components["schemas"]["PreviewTraceEventKind"];
export type JsonValue = components["schemas"]["JsonValue"];

/** The one event family this leaf reads, spelled from the generated contract's union. */
export const PROMPT_CALL_KIND = "prompt_call" as const satisfies PreviewTraceEventKind;

/**
 * Brain's closed vocabularies for the members this build branches on,
 * transcribed from the producer contract (`clinical-preview-trace.v1`,
 * `$defs.eventCommon`, `$defs.content`, `$defs.message`, `$defs.invocation`).
 * Each is CLOSED on purpose: a value outside it is not a new state to invent a
 * sentence for, it is an event this build cannot read.
 */
export const EVENT_VISIBILITIES = [
  "clinical_full",
  "protected_clinical",
  "restricted_technical",
] as const;
export type EventVisibility = (typeof EVENT_VISIBILITIES)[number];

export const EVENT_STATUSES = [
  "ok",
  "no_action",
  "attention",
  "failed",
  "unavailable",
] as const;
export type EventStatus = (typeof EVENT_STATUSES)[number];

export const CONTENT_STATES = ["inline", "reference", "omitted"] as const;
export type ContentState = (typeof CONTENT_STATES)[number];

export const CONTENT_ENCODINGS = ["text/plain", "application/json"] as const;
export type ContentEncoding = (typeof CONTENT_ENCODINGS)[number];

export const MESSAGE_ROLES = [
  "system",
  "developer",
  "user",
  "assistant",
  "tool",
] as const;
export type MessageRole = (typeof MESSAGE_ROLES)[number];

export const INVOCATION_OUTCOMES = ["invoked", "not_invoked", "failed"] as const;
export type InvocationOutcome = (typeof INVOCATION_OUTCOMES)[number];

/** What a body's declared size says about the text actually received. */
export type Exactness =
  /** The declared byte size equals the UTF-8 length of the text received. */
  | "exact"
  /** The producer declared a size and the text received measures differently. Never presented as exact. */
  | "size_mismatch"
  /** No size was declared, so exactness cannot be stated either way. */
  | "size_unstated";

/** The facts every content slot carries whether or not it carries text. */
export type ContentFacts = {
  contentHash: string | null;
  byteSize: number | null;
  encoding: ContentEncoding | null;
  visibility: EventVisibility | null;
};

/**
 * One clinical body, in exactly one of the contract's three states. `inline`
 * carries the text VERBATIM — every byte the response carried, untouched.
 */
export type ContentBody =
  | ({
      state: "inline";
      text: string;
      /** UTF-8 length of `text` as received, measured here. */
      measuredBytes: number;
      exactness: Exactness;
    } & ContentFacts)
  | ({ state: "reference" } & ContentFacts)
  | ({ state: "omitted"; reason: OmissionReason | null } & ContentFacts);

export type PromptCallCoordinate = {
  turnNumber: number | null;
  phase: string | null;
  track: string | null;
  agent: string | null;
  step: string | null;
};

export type PromptCallMessage = { role: MessageRole; content: ContentBody };

/** The producer's own identity strings for the model that answered; each axis null when not carried. */
export type CallModelIdentity = {
  model: string | null;
  modelDeployment: string | null;
};

export type Invocation = {
  outcome: InvocationOutcome;
  reasonCode: string | null;
  latencyMs: number | null;
};

/** One `prompt_call` event as this build reads it — compact or detailed, the same shape. */
export type PromptCall = {
  eventRef: string;
  sequence: number | null;
  occurredAt: string | null;
  visibility: EventVisibility;
  label: string | null;
  status: EventStatus;
  coordinate: PromptCallCoordinate;
  callType: string | null;
  /** Null when the event carries no slot at all — "not reported", never "empty". */
  systemPrompt: ContentBody | null;
  messages: readonly PromptCallMessage[];
  structuredResult: ContentBody | null;
  patientFacingOutput: ContentBody | null;
  modelIdentity: CallModelIdentity | null;
  invocation: Invocation;
};

export type PromptCallReading =
  | { kind: "readable"; call: PromptCall }
  /**
   * `at` is a CONTENT-FREE coordinate — a member path such as
   * `detail.messages[2].content.state` — never the value found there. It may
   * be rendered, and a body could be what sits at that path.
   */
  | { kind: "unreadable"; at: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** An integer or null — the producer's R25 accepts an integral number in either spelling. */
function integerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

function memberOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return typeof value === "string" && (list as readonly string[]).includes(value);
}

/**
 * The UTF-8 length of a string, counted here rather than by encoding it, so
 * the answer is the same in every runtime this module runs in. `for…of`
 * walks code points, so an astral character (an emoji, a CJK extension) is
 * one 4-byte step and never two 3-byte halves. A lone surrogate — which the
 * Editor Service already refuses upstream — counts as the 3-byte replacement
 * an encoder would emit for it.
 */
export function utf8ByteLength(text: string): number {
  let bytes = 0;
  for (const character of text) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes += codePoint < 0x80 ? 1 : codePoint < 0x800 ? 2 : codePoint < 0x10000 ? 3 : 4;
  }
  return bytes;
}

/**
 * The refusal every reader on this lane raises, carrying a CONTENT-FREE member
 * path and never the value found there.
 *
 * Exported for `previewPromptProvenance.ts` (#1101), which reads the same
 * bodies out of a different event family. A second copy of {@link readBody}'s
 * policy is the one thing that must not exist twice on this lane: it is what
 * refuses a restricted slot that nevertheless carries text, and a withheld
 * body that still names a reference to fetch.
 */
export class Unreadable extends Error {
  constructor(readonly at: string) {
    super(`unreadable prompt call at ${at}`);
  }
}

/**
 * A member of a closed vocabulary: absent or null reads as "not reported";
 * PRESENT AND OUTSIDE THE LIST is refused. The two are different facts —
 * `encoding: "base64"` is not an encoding this build has no opinion about,
 * it is a body this build would render as the wrong thing — and a reader
 * that folded the second into the first would render a value it does not
 * understand as if the producer had said nothing (#1121 review).
 */
function closedOrNull<T extends string>(
  list: readonly T[],
  value: unknown,
  at: string,
): T | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!memberOf(list, value)) {
    throw new Unreadable(at);
  }
  return value;
}

function readFacts(slot: Record<string, unknown>, at: string): ContentFacts {
  const byteSize = integerOrNull(slot["byteSize"]);
  return {
    contentHash: stringOrNull(slot["contentHash"]),
    byteSize: byteSize !== null && byteSize >= 0 ? byteSize : null,
    encoding: closedOrNull(CONTENT_ENCODINGS, slot["encoding"], `${at}.encoding`),
    visibility: closedOrNull(EVENT_VISIBILITIES, slot["visibility"], `${at}.visibility`),
  };
}

/**
 * One content slot. `null` and absence both read as "no slot" (the caller
 * decides what that means for its field); anything else must be one of the
 * contract's three states, with the text present exactly when the state
 * says it is. A `reference` or `omitted` slot that nevertheless carries text
 * is not read tolerantly: a body where the contract says there is none is the
 * one shape that could put restricted content on screen, so it fails closed.
 *
 * RESTRICTION IS THE SLOT'S OWN FACT, not only the event's. The producer's
 * R8 withholds a `restricted_technical` body at source with no reference to
 * fetch, whatever the event around it is classified; so a slot that says it
 * is restricted and nevertheless carries text, or a reference to fetch, is a
 * defect upstream and is refused here rather than rendered — an event-level
 * check alone would let a `clinical_full` event carry a restricted body onto
 * the screen (#1121 review, finding 2).
 */
export function readBody(value: unknown, at: string): ContentBody | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    throw new Unreadable(at);
  }
  const state = value["state"];
  if (!memberOf(CONTENT_STATES, state)) {
    throw new Unreadable(`${at}.state`);
  }
  const facts = readFacts(value, at);
  const omittedReason = closedOrNull(OMISSION_REASONS, value["omittedReason"], `${at}.omittedReason`);
  // R8 and R11 make restriction symmetric: selecting the permissive arm of a
  // contradictory body could render bytes another member says were withheld.
  // Refuse the whole body instead of choosing which upstream fact to trust.
  if (state !== "omitted" && omittedReason !== null) {
    throw new Unreadable(`${at}.omittedReason`);
  }
  if (facts.visibility === "restricted_technical" && state !== "omitted") {
    throw new Unreadable(`${at}.visibility`);
  }
  if (facts.visibility === "restricted_technical" && omittedReason !== "restricted_technical") {
    throw new Unreadable(`${at}.omittedReason`);
  }
  if (omittedReason === "restricted_technical" && facts.visibility !== "restricted_technical") {
    throw new Unreadable(`${at}.visibility`);
  }
  const inline = value["inline"];
  if (state === "inline") {
    if (typeof inline !== "string") {
      throw new Unreadable(`${at}.inline`);
    }
    const measuredBytes = utf8ByteLength(inline);
    return {
      state,
      text: inline,
      measuredBytes,
      exactness:
        facts.byteSize === null
          ? "size_unstated"
          : facts.byteSize === measuredBytes
            ? "exact"
            : "size_mismatch",
      ...facts,
    };
  }
  if (inline !== null && inline !== undefined) {
    throw new Unreadable(`${at}.inline`);
  }
  if (state === "reference") {
    return { state, ...facts };
  }
  // An omitted body carries NO reference (the producer's R11): a withheld
  // body with something to fetch is precisely what "restricted content must
  // not become reachable by guessing a reference" forbids, so a reference
  // here is refused rather than ignored (review round 2, finding 3).
  const contentRef = value["contentRef"];
  if (contentRef !== null && contentRef !== undefined) {
    throw new Unreadable(`${at}.contentRef`);
  }
  return {
    state,
    reason: omittedReason,
    ...facts,
  };
}

function readCoordinate(value: unknown): PromptCallCoordinate {
  const source = isRecord(value) ? value : {};
  const turnNumber = integerOrNull(source["turnNumber"]);
  return {
    turnNumber: turnNumber !== null && turnNumber >= 1 ? turnNumber : null,
    phase: stringOrNull(source["phase"]),
    track: stringOrNull(source["track"]),
    agent: stringOrNull(source["agent"]),
    step: stringOrNull(source["step"]),
  };
}

function readModelIdentity(value: unknown): CallModelIdentity | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (!isRecord(value)) {
    throw new Unreadable("detail.modelIdentity");
  }
  return {
    model: stringOrNull(value["model"]),
    modelDeployment: stringOrNull(value["modelDeployment"]),
  };
}

function readInvocation(value: unknown): Invocation {
  if (!isRecord(value)) {
    throw new Unreadable("detail.invocation");
  }
  const outcome = value["outcome"];
  if (!memberOf(INVOCATION_OUTCOMES, outcome)) {
    throw new Unreadable("detail.invocation.outcome");
  }
  const latencyMs = integerOrNull(value["latencyMs"]);
  return {
    outcome,
    reasonCode: stringOrNull(value["reasonCode"]),
    latencyMs: latencyMs !== null && latencyMs >= 0 ? latencyMs : null,
  };
}

function readMessages(value: unknown): PromptCallMessage[] {
  if (value === null || value === undefined) {
    return [];
  }
  if (!Array.isArray(value)) {
    throw new Unreadable("detail.messages");
  }
  return value.map((entry, index) => {
    const at = `detail.messages[${index}]`;
    if (!isRecord(entry)) {
      throw new Unreadable(at);
    }
    const role = entry["role"];
    if (!memberOf(MESSAGE_ROLES, role)) {
      throw new Unreadable(`${at}.role`);
    }
    const content = readBody(entry["content"], `${at}.content`);
    if (content === null) {
      throw new Unreadable(`${at}.content`);
    }
    return { role, content };
  });
}

/**
 * Reads one event — compact or detailed — into `PromptCall`, or says at which
 * member it could not.
 *
 * Two readings are deliberately strict beyond the member-by-member checks:
 *
 *  - the event must be a `prompt_call`. The locator asked for that family and
 *    the service promises the filter; anything else is a page this build must
 *    not present as the first prompt;
 *  - a `restricted_technical` event carries no readable body in any slot
 *    (the producer's R8). One that does is refused whole rather than shown:
 *    the browser never redacts, and it never shows what policy withheld at
 *    source either — an inline body under a restricted event is a defect
 *    upstream, not content to render.
 */
export function readPromptCall(event: unknown): PromptCallReading {
  try {
    if (!isRecord(event)) {
      throw new Unreadable("event");
    }
    if (event["kind"] !== PROMPT_CALL_KIND) {
      throw new Unreadable("kind");
    }
    const eventRef = event["eventRef"];
    if (typeof eventRef !== "string" || eventRef.length === 0) {
      throw new Unreadable("eventRef");
    }
    const visibility = event["visibility"];
    if (!memberOf(EVENT_VISIBILITIES, visibility)) {
      throw new Unreadable("visibility");
    }
    const status = event["status"];
    if (!memberOf(EVENT_STATUSES, status)) {
      throw new Unreadable("status");
    }
    const detail = event["detail"];
    if (!isRecord(detail)) {
      throw new Unreadable("detail");
    }
    const sequence = integerOrNull(event["sequence"]);
    const call: PromptCall = {
      eventRef,
      sequence: sequence !== null && sequence >= 1 ? sequence : null,
      occurredAt: stringOrNull(event["occurredAt"]),
      visibility,
      label: stringOrNull(event["label"]),
      status,
      coordinate: readCoordinate(event["coordinate"]),
      callType: stringOrNull(detail["callType"]),
      systemPrompt: readBody(detail["systemPrompt"], "detail.systemPrompt"),
      messages: readMessages(detail["messages"]),
      structuredResult: readBody(
        detail["structuredResult"],
        "detail.structuredResult",
      ),
      patientFacingOutput: readBody(
        detail["patientFacingOutput"],
        "detail.patientFacingOutput",
      ),
      modelIdentity: readModelIdentity(detail["modelIdentity"]),
      invocation: readInvocation(detail["invocation"]),
    };
    if (visibility === "restricted_technical" && bodiesOf(call).some(isInline)) {
      throw new Unreadable("visibility");
    }
    return { kind: "readable", call };
  } catch (error) {
    if (error instanceof Unreadable) {
      return { kind: "unreadable", at: error.at };
    }
    throw error;
  }
}

/** Every content slot the call carries, in the order the panel renders them. */
export function bodiesOf(call: PromptCall): ContentBody[] {
  return [
    call.systemPrompt,
    ...call.messages.map((message) => message.content),
    call.structuredResult,
    call.patientFacingOutput,
  ].filter((body): body is ContentBody => body !== null);
}

export function isInline(
  body: ContentBody,
): body is Extract<ContentBody, { state: "inline" }> {
  return body.state === "inline";
}

/** Whether two bodies are the same bytes, by the producer's digest — never by comparing text. */
export function sameDigest(a: ContentBody | null, b: ContentBody | null): boolean {
  return (
    a !== null &&
    b !== null &&
    a.contentHash !== null &&
    b.contentHash !== null &&
    a.contentHash === b.contentHash
  );
}

/** The detail read, checked against the event it was asked for. */
export type PromptCallDetailReading =
  | { kind: "content_unavailable"; run: PreviewRunState }
  | { kind: "unreadable"; at: string; run: PreviewRunState }
  /** The service answered a different event than the one addressed. Fail closed. */
  | { kind: "mismatch"; run: PreviewRunState }
  | { kind: "readable"; call: PromptCall; run: PreviewRunState };

export function readPromptCallDetail(
  response: PreviewRunEventDetailResponse,
  expectedEventRef: string,
): PromptCallDetailReading {
  const { run } = response;
  const event = response.event ?? null;
  if (!run.contentAvailable) {
    return { kind: "content_unavailable", run };
  }
  if (event === null) {
    return { kind: "unreadable", at: "event", run };
  }
  const reading = readPromptCall(event);
  if (reading.kind === "unreadable") {
    return { kind: "unreadable", at: `event.${reading.at}`, run };
  }
  if (reading.call.eventRef !== expectedEventRef) {
    return { kind: "mismatch", run };
  }
  return { kind: "readable", call: reading.call, run };
}

/**
 * The Unicode bidirectional control characters (the `Bidi_Control` property:
 * U+061C, U+200E, U+200F, U+202A–U+202E, U+2066–U+2069), each with the name
 * the panel shows for it. Spelled as explicit code points rather than as a
 * property escape so the set this build marks is the set this file states —
 * the test pins it against the runtime's own `\p{Bidi_Control}`.
 */
export const BIDI_CONTROL_NAMES: ReadonlyMap<string, string> = new Map([
  ["\u061C", "Arabic letter mark"],
  ["\u200E", "left-to-right mark"],
  ["\u200F", "right-to-left mark"],
  ["\u202A", "left-to-right embedding"],
  ["\u202B", "right-to-left embedding"],
  ["\u202C", "pop directional formatting"],
  ["\u202D", "left-to-right override"],
  ["\u202E", "right-to-left override"],
  ["\u2066", "left-to-right isolate"],
  ["\u2067", "right-to-left isolate"],
  ["\u2068", "first strong isolate"],
  ["\u2069", "pop directional isolate"],
]);

const BIDI_CONTROL_PATTERN =
  /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

export type InertTextSegment =
  | { kind: "text"; text: string }
  | {
      kind: "bidi_control";
      /** The character itself — kept in the rendered text so the body stays byte-exact. */
      character: string;
      /** `U+202E`, for the visible marker. */
      codePoint: string;
      name: string;
    };

/** `U+202E`-style spelling of one character's code point. */
export function codePointLabel(character: string): string {
  return `U+${(character.codePointAt(0) ?? 0).toString(16).toUpperCase().padStart(4, "0")}`;
}

/**
 * Splits a body around its bidirectional control characters, so each one can
 * be rendered inside its own isolated, visibly marked element while the text
 * between them is rendered as it is.
 *
 * ROUND-TRIPS EXACTLY: concatenating the segments' characters yields the
 * input. Nothing is dropped, replaced or reordered — the marker is a matter
 * of presentation, and the bytes on screen remain the bytes received.
 */
export function segmentBidiControls(text: string): InertTextSegment[] {
  const segments: InertTextSegment[] = [];
  let cursor = 0;
  for (const match of text.matchAll(BIDI_CONTROL_PATTERN)) {
    const index = match.index;
    const character = match[0];
    if (index > cursor) {
      segments.push({ kind: "text", text: text.slice(cursor, index) });
    }
    segments.push({
      kind: "bidi_control",
      character,
      codePoint: codePointLabel(character),
      name: BIDI_CONTROL_NAMES.get(character) ?? "bidirectional control",
    });
    cursor = index + character.length;
  }
  if (cursor < text.length) {
    segments.push({ kind: "text", text: text.slice(cursor) });
  }
  return segments;
}

/** Whether a body contains any bidirectional control character. */
export function hasBidiControls(text: string): boolean {
  return new RegExp(BIDI_CONTROL_PATTERN.source, "u").test(text);
}

export const VISIBILITY_LABELS = {
  clinical_full: "Clinical content, shown in full",
  protected_clinical: "Protected clinical content, read-only",
  restricted_technical: "Restricted technical content, withheld at source",
} as const satisfies Record<EventVisibility, string>;

export const EVENT_STATUS_LABELS = {
  ok: "OK",
  no_action: "No action",
  attention: "Attention",
  failed: "Failed",
  unavailable: "Unavailable",
} as const satisfies Record<EventStatus, string>;

export const EVENT_STATUS_TONES = {
  ok: "success",
  no_action: "neutral",
  attention: "warning",
  failed: "destructive",
  unavailable: "destructive",
} as const satisfies Record<
  EventStatus,
  "outline" | "success" | "neutral" | "warning" | "destructive"
>;

export const MESSAGE_ROLE_LABELS = {
  system: "System",
  developer: "Developer",
  user: "User",
  assistant: "Assistant",
  tool: "Tool",
} as const satisfies Record<MessageRole, string>;

export const INVOCATION_LABELS = {
  invoked: "Model invoked",
  not_invoked: "Model not invoked",
  failed: "Model call failed",
} as const satisfies Record<InvocationOutcome, string>;

export const CONTENT_ENCODING_LABELS = {
  "text/plain": "plain text",
  "application/json": "JSON",
} as const satisfies Record<ContentEncoding, string>;

/** One sentence per invocation outcome, stating the recorded fact and its reason code where one was recorded. */
export function describeInvocation(invocation: Invocation): string {
  switch (invocation.outcome) {
    case "invoked":
      return invocation.latencyMs !== null
        ? `The model was invoked and answered in ${invocation.latencyMs} ms.`
        : "The model was invoked.";
    case "not_invoked":
      return invocation.reasonCode !== null
        ? `The model was not called for this step. Recorded reason: ${invocation.reasonCode}.`
        : "The model was not called for this step. No reason code was recorded.";
    case "failed":
      return invocation.reasonCode !== null
        ? `The model call failed. Recorded reason: ${invocation.reasonCode}.`
        : "The model call failed. No reason code was recorded.";
  }
}

/** Why a body is not shown, from the producer's own reason and nothing else. */
export function describeOmission(
  body: Extract<ContentBody, { state: "omitted" }>,
): string {
  if (body.reason === "restricted_technical") {
    return "Withheld at source: restricted technical content. Policy keeps this body out of every projection; its size and digest are shown so the omission is verifiable, and no reference to it exists.";
  }
  if (body.reason !== null) {
    return `Withheld: ${OMISSION_REASON_LABELS[body.reason]}.`;
  }
  return "Withheld, and the producer recorded no reason.";
}

/** What the body's declared size says about the text received, as one sentence. */
export function describeExactness(
  body: Extract<ContentBody, { state: "inline" }>,
): string {
  switch (body.exactness) {
    case "exact":
      return `Exact bytes: ${body.measuredBytes} bytes received, ${body.byteSize ?? body.measuredBytes} declared.`;
    case "size_mismatch":
      return `Not presented as exact: ${body.measuredBytes} bytes received, but the producer declared ${body.byteSize ?? 0}.`;
    case "size_unstated":
      return `${body.measuredBytes} bytes received; the producer declared no size, so exactness is not asserted.`;
  }
}
